import type { ForwardRefRenderFunction } from "react"
import React, { forwardRef, useImperativeHandle, useCallback, useEffect } from "react"
import { useSetState } from "ahooks-v2"
import { Checkbox, Row, Col } from "antd"
import type { ChoiceGroupDataVO } from "@/api"
import { isInvalid, getDiffOfArrays } from "@/util/util"
import "./index.scss"
// ==============================================
const CheckboxGroup = Checkbox.Group
// ==============================================
interface stateVO {
  options: (string | number)[]
}
interface ChoiceGroupVO {
  data: ChoiceGroupDataVO
  changeCallback?: () => void // 值改变时的回调
}
const ChoiceGroup: ForwardRefRenderFunction<any, ChoiceGroupVO> = (
  { data, changeCallback },
  ref,
) => {
  const { title = "", list, colSpan = 8, mode = "multiple", unlimited, defaultValues = [] } = data
  const [state, set] = useSetState<stateVO>({
    options: defaultValues,
  })

  // 选择change
  const checkboxChange = useCallback(
    (values) => {
      let result: string[] = values
      const cloneValues = values.slice()
      // 多选
      if (mode === "multiple") {
        // 进行不限判断
        if (!isInvalid(unlimited)) {
          set((prev) => {
            const index = values.indexOf(unlimited)
            const currentChoice = getDiffOfArrays(prev.options, values)?.[0]
            if (
              currentChoice === unlimited
              // (values?.length === list?.length - 1 && index < 0)
            ) {
              result = [unlimited!]
            } else if (index >= 0) {
              cloneValues.splice(index, 1)
              result = cloneValues
            }
            return {
              options: result,
            }
          })
        } else {
          set({
            options: result,
          })
        }
      } else {
        // 单选
        set((prev) => {
          if (prev.options?.length) {
            const oldValue = prev.options?.[0]
            const index = values.indexOf(oldValue)
            if (index >= 0) {
              cloneValues.splice(index, 1)
              result = cloneValues
            }
          }
          return {
            options: result,
          }
        })
      }
      changeCallback && changeCallback()
    },
    [mode, unlimited, set, changeCallback],
  )

  // 获取值
  const getValues = useCallback(() => {
    const names: string[] = []
    for (const value of state.options || []) {
      list.filter((item) => item.value === value && names.push(item.label))
    }
    return {
      options: state.options,
      names,
    }
  }, [state.options, list])

  // 设置值
  const setValues = useCallback(
    (values) => {
      set({
        options: values,
      })
    },
    [set],
  )

  // 清空值
  const clearValues = useCallback(() => {
    set({
      options: [],
    })
  }, [set])

  // 传递方法到父组件
  useImperativeHandle(ref, () => ({
    getValues,
    setValues,
    clearValues,
  }))

  // 设置值
  useEffect(() => {
    set({
      options: defaultValues,
    })
  }, [defaultValues, set])

  return data ? (
    <div className="choice-group">
      <div className="title">{title}</div>
      <CheckboxGroup
        className="theme-checkbox-group"
        value={state.options}
        onChange={(values) => checkboxChange(values)}
      >
        <Row>
          {list?.map((subItem) => (
            <Col span={colSpan} key={subItem.value}>
              <Checkbox className="theme-checkbox-highlight" value={subItem.value}>
                {subItem.label}
              </Checkbox>
            </Col>
          ))}
        </Row>
      </CheckboxGroup>
    </div>
  ) : null
}

export default forwardRef(ChoiceGroup)
